import React from 'react';
import { Modal } from '../components';

type LoginSystem = 'hub' | 'dashboard';

interface LoginAlertsProps {
    system: LoginSystem;
    downtime: boolean;
    warning: boolean;
    warningMsg: string;
    warningStart: string;
    scheduled: boolean;
    scheduledStart: string;
    scheduledEnd: string;
    scheduledStartDay: string;
    scheduledEndDay: string;
    overtime: boolean;
}

// Alternate wording for each login screen
const systemDescriptions: Record<LoginSystem, {
    accessDescription: string;
}> = {
    hub: {
        accessDescription: 'the Training Hub',
    },
    dashboard: {
        accessDescription: 'the dashboard',
    },
};

const LoginAlerts: React.FC<LoginAlertsProps> = ({
    system,
    downtime,
    warning,
    warningMsg,
    warningStart,
    scheduled,
    scheduledStart,
    scheduledEnd,
    scheduledStartDay,
    scheduledEndDay,
    overtime,
}) => {
    const description = systemDescriptions[system];

    return (
        <>
            {/* Down for Maintenance */}
            <div className="toast show bg-body-secondary border-2 border-danger position-relative rounded-3 animate-alert-pulse" style={{ width: '100%', maxWidth: '450px', margin: '0 auto'}} hidden={!downtime} role="alert" aria-live="assertive" aria-atomic="true">
                <div className="toast-header">
                    <strong className="me-auto">
                        <i className="fa-regular fa-ban me-1" style={{ color: '#CC0202' }}/>Down for Maintenance
                    </strong>
                    <small className="text-success" data-bs-toggle="modal" data-bs-target="#maintenanceInfoModal" style={{ cursor: 'pointer' }}>
                        <u>More Info</u>
                    </small>
                </div>

                <div className="toast-body">
                    <h3>
                        The EEO Training system is currently undergoing maintenance, which is scheduled to end{' '}
                        {scheduledEnd === '' || overtime ? 'shortly' : 'at approximately ' + scheduledEnd + ' EST on ' + scheduledEndDay}
                        . Please come back later to access{' '} {description.accessDescription}, we apologize for the inconvenience.
                    </h3>
                </div>
            </div>

            {/* Scheduled Maintenance */}
            <div className="toast show bg-body-secondary border-2 border-warning position-relative rounded-3" style={{ width: '100%', maxWidth: '450px', margin: '0 auto' }}
                hidden={!scheduled} role="alert" aria-live="assertive" aria-atomic="true">
                <div className="toast-header">
                    <strong className="me-auto">
                        <i className="fa-regular fa-triangle-exclamation me-2" style={{ color: '#FFD43B' }}/>Scheduled Maintenance
                    </strong>
                    <small className="text-success" data-bs-toggle="modal" data-bs-target="#maintenanceInfoModal" style={{ cursor: 'pointer' }}>
                        <u>More Info</u>
                    </small>
                    <button type="button" className="btn-close ms-2 mb-1" data-bs-dismiss="toast" aria-label="Close">
                        <span aria-hidden="true"></span>
                    </button>
                </div>

                <div hidden={scheduledEnd !== ''} className="toast-body">
                    The EEO Training system is planned to undergo scheduled maintenance{' '}
                    {scheduledStart === '' ? 'soon' : 'at approximately ' + scheduledStart + ' EST, on ' + scheduledStartDay}
                    , which will temporarily restrict access to{' '} {description.accessDescription} for a period of downtime that usually
                    lasts no longer than 20 minutes. We apologize in advance for any inconvenience.
                </div>

                <div hidden={scheduledEnd === '' || scheduledStart === ''} className="toast-body">
                    The EEO Training system is planned to undergo scheduled maintenance on{' '}
                    {scheduledStartDay}, from approximately{' '} {scheduledStart + ' EST '} to {scheduledEnd + ' EST'}{' '}
                    {scheduledStartDay === scheduledEndDay ? '' : ', ' + scheduledEndDay}
                    . Access to {description.accessDescription} will be restricted during this downtime and we apologize in advance for any inconvenience.
                </div>

                <div hidden={scheduledEnd === '' || scheduledStart !== ''} className="toast-body">
                    The EEO Training system is planned to undergo scheduled maintenance soon, which is projected to conclude at approximately{' '}
                    {scheduledEnd + ' EST '} on {scheduledEndDay}. Access to{' '} {description.accessDescription} will be restricted during this
                    downtime and we apologize in advance for any inconvenience.
                </div>
            </div>

            {/* System Issues */}
            <div className="toast show bg-body-secondary border-2 border-warning position-relative rounded-3"
                style={{ width: '100%', maxWidth: '450px', margin: '0 auto' }} hidden={!warning} role="alert" aria-live="assertive" aria-atomic="true">
                <div className="toast-header">
                    <strong className="me-auto">
                        <i className="fa-regular fa-triangle-exclamation me-2" style={{ color: '#FFD43B' }}/>System Issues
                    </strong>

                    <small>{warningStart} EST</small>

                    <button type="button" className="btn-close ms-2 mb-1" data-bs-dismiss="toast" aria-label="Close">
                        <span aria-hidden="true"></span>
                    </button>
                </div>

                <div className="toast-body">
                    {warningMsg}
                </div>
            </div>

            {/* Maintenance Info Modal */}
            <Modal id="maintenanceInfoModal" title="Maintenance and Downtime" size="xl">
                <div className="modal-body">
                    <h5>
                        <strong>
                            Why can't I access the system during maintenance?
                        </strong>
                    </h5>

                    <p className="text-light">
                        We often perform maintenance in order to deploy updates to the EEO Training system that adds new features and upgrades
                        which makes the experience better for you and everyone taking trainings. While these kinds of changes are still being
                        installed, interactions with the system have the potential to cause unexpected results or inconsistencies. By restricting
                        system usage during this time, we can ensure the smooth deployment of updates without risking such an outcome.
                    </p>

                    <br />

                    <h5>
                        <strong>
                            Why was maintenance scheduled at this time?
                        </strong>
                    </h5>

                    <p className="text-light">
                        When we carry out maintenance, we try to schedule the downtime during business hours at our office in the Eastern time zone.
                        We typically aim for the early or later hours of the day to avoid affecting as many users as possible. However, as an
                        international service offered in multiple time zones, EEO Training may not always experience downtime at a convenient
                        time for you. When we have planned downtime such as this, it usually lasts less than a few minutes.
                    </p>

                    <br />

                    <h5>
                        <strong>
                            I don't see any new features, what was the downtime for?
                        </strong>
                    </h5>

                    <p className="text-light">
                        Sometimes, our updates might not include additions that are immediately visible for you. We might be fixing minor bugs,
                        making security improvements, or improving loading times. Some new features are only for internal use, which allows us to
                        operate more efficiently and effectively in the background.
                    </p>
                </div>

                <div className="modal-footer">
                    <button type="button" className="btn btn-secondary" data-bs-dismiss="modal">
                        Close
                    </button>
                </div>
            </Modal>
            <br />
        </>
    );
};

export default LoginAlerts;
